// BomPreviewDataGrid — local-tab preview table built on @mui/x-data-grid.
//
// Replaces the hand-rolled MUI Table. The high-priority per-row controls
// (Type, Docs, Discovery) live in the leftmost columns; mapped data
// columns follow; Confidence pins to the far right. The DataGrid handles
// horizontal scrolling, sticky header, density, column visibility, sort,
// and quick filter out-of-box — affordances engineers need to *review*
// a 50-row BOM before confirming.
//
// Central-sheet tab still uses the legacy Table component — that view is
// rarely used and doesn't justify a parallel migration.
import React from 'react';
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  type GridColDef,
  type GridRenderCellParams,
  type GridSortModel,
} from '@mui/x-data-grid';
import {
  Box,
  ButtonBase,
  Chip,
  Checkbox,
  IconButton,
  InputBase,
  Select,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type {
  ColumnMapping,
  LineGoals,
  ParsedRow,
  PartClassificationCode,
  PendingDocument,
  PreviewDocumentType,
  SourcingModeCode,
} from '../../types';
import { T } from '../../components/portfolio/PortfolioParts';
import { ConfigurationSelect, type BomConfiguration } from '../../components/bom-v2/ConfigurationSelect';
import { PreviewLineDetail } from './PreviewLineDetail';
import { IBM_PLEX_MONO } from '../../theme/ibmPlex';
import { buildPreviewTree, descendantsOf, isLostText, lineKind, lineNotes, quantityIn, type LineKind } from './preview-tree';

import {
  SourcingPolicyControl,
  type AlternativesSearchChoice,
  type ComplianceCheckChoice,
} from '../../components/sourcing/SourcingPolicyControl';
import {
  SourcingConstraints,
  ConstraintMode,
  effectiveForLine,
} from '../../types/sourcingConstraints';

export interface PartClassificationOption {
  value: PartClassificationCode;
  label: string;
  short: string;
  color: string;
}

// Column order and alignment of the lines table.
const LEAD_FIELDS = ['__include', '__num', '__status'];
const PACKAGE_IDENTITY_FIELDS = ['__item', '__description', '__rev', '__qty', '__material', '__mpn', '__price'];
const SETTING_FIELDS = ['__type', '__docs', '__policy'];
const IDENTITY_TARGETS = ['itemNumber', 'findNumber', 'partNumber', 'localCode', 'manufacturerName', 'mpn', 'partDescription', 'quantity'];
const GOAL_TARGETS = new Set(['requiredQuantity', 'targetPrice', 'targetLeadTime']);
const NUMERIC_TARGETS = new Set(['quantity', 'unitCost']);

/** A cell of the file as a number: '$3.90' and '1,200' read as 3.9 and 1200. */
const cellNumber = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined;
  const n = Number(String(value).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

/** Days per unit of a lead-time column, from its header (weeks, months, else days). Mirrors the backend. */
const leadTimeUnitDays = (header?: string): number => {
  const h = (header ?? '').toLowerCase();
  if (/\b(wk|wks|week|weeks)\b/.test(h)) return 7;
  if (/\b(mo|mos|month|months)\b/.test(h)) return 30;
  return 1;
};

/** One optional goal of a line, typed in place. Saved when the cell is left or Enter is pressed. */
const GoalCell: React.FC<{
  value: number | undefined;
  placeholder?: string;
  label: string;
  onCommit: (next: number | null) => void;
}> = ({ value, placeholder, label, onCommit }) => {
  const shown = value != null ? String(value) : '';
  const [text, setText] = React.useState(shown);
  React.useEffect(() => setText(shown), [shown]);
  const commit = () => {
    const trimmed = text.trim();
    if (trimmed === shown) return;
    const next = trimmed === '' ? null : Number(trimmed.replace(/[$,\s]/g, ''));
    if (next !== null && !(Number.isFinite(next) && next >= 0)) {
      setText(shown);
      return;
    }
    onCommit(next);
  };
  return (
    <InputBase
      value={text}
      placeholder={placeholder}
      inputProps={{ 'aria-label': label, inputMode: 'decimal', style: { textAlign: 'right' } }}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      // The grid takes typing, arrows and space for its own navigation.
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setText(shown);
      }}
      onClick={(e) => e.stopPropagation()}
      sx={{
        width: '100%',
        fontSize: '0.8rem',
        px: 0.75,
        borderRadius: 1,
        border: '1px solid transparent',
        '&:hover': { borderColor: '#e2e8f0' },
        fontFamily: PREVIEW_MONO,
        '&.Mui-focused': { borderColor: T.navy, bgcolor: '#fff' },
        '& input::placeholder': { color: '#94a3b8', opacity: 1 },
      }}
    />
  );
};

/** A small, light checkbox: a thin border, and a navy tick when on. */
const boxSx = { width: 14, height: 14, borderRadius: '4px', border: '1.25px solid #CBD5E1', bgcolor: '#fff', display: 'grid', placeItems: 'center' } as const;
const tick = (fill: boolean) => (
  <Box sx={{ ...boxSx, ...(fill ? { bgcolor: T.navy, borderColor: T.navy } : {}) }}>
    <Box sx={{ width: 7, height: 4, border: `solid ${fill ? '#fff' : T.navy}`, borderWidth: '0 0 1.5px 1.5px', transform: 'rotate(-45deg) translate(1px, -1px)' }} />
  </Box>
);
const SmallCheckbox: React.FC<React.ComponentProps<typeof Checkbox>> = (props) => (
  <Checkbox
    {...props}
    disableRipple
    icon={<Box sx={boxSx} />}
    checkedIcon={tick(false)}
    indeterminateIcon={<Box sx={{ ...boxSx, bgcolor: T.navy, borderColor: T.navy }}><Box sx={{ width: 6, height: 1.5, bgcolor: '#fff' }} /></Box>}
    sx={{ p: 0.5, '&:hover .MuiBox-root, .Mui-focusVisible &': { borderColor: T.muted }, ...(props.sx ?? {}) }}
  />
);

/** The row that heads the lines placed on no assembly. Not a line of the file. */
type GridRow = ParsedRow & { __group?: true };
const GROUP_ROW_INDEX = -1;
const PREVIEW_MONO = IBM_PLEX_MONO;
const GROUP_KEY = 'unplaced';
type KindFilter = 'all' | LineKind | 'attention' | 'edited';
const KIND_FILTERS: Array<{ key: KindFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'assembly', label: 'Assemblies' },
  { key: 'make', label: 'Make' },
  { key: 'buy', label: 'Buy' },
  { key: 'attention', label: 'To check' },
  { key: 'edited', label: 'Edited' },
];
const isEdited = (r: ParsedRow) => Object.keys(r.originalCells ?? {}).length > 0;
const needsAttention = (r: ParsedRow) => lineNotes(r).some((n) => !n.info);
const sameText = (a: unknown, b: unknown) =>
  String(a ?? '').replace(/\s+/g, ' ').trim().toLowerCase() === String(b ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

interface PreviewToolbarProps {
  configurations?: BomConfiguration[];
  configurationKey?: string;
  onConfigurationChange: (key: string) => void;
  configurationCounts: Record<string, number>;
  kindFilter: string;
  onKindFilter: (key: KindFilter) => void;
  kindCounts: Record<string, number>;
  variantOnly: boolean;
  onVariantOnly: (on: boolean) => void;
  hasTree: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  search: string;
  onSearch: (q: string) => void;
}

/** The table's toolbar: variant, what to show, and the grid's own column, filter and density tools. */
const PreviewToolbar: React.FC<PreviewToolbarProps> = (p) => (
  <GridToolbarContainer
    sx={{
      px: 1.75,
      py: 1.25,
      gap: 1.25,
      flexWrap: 'wrap',
      borderBottom: `1px solid ${T.line}`,
      // The grid's column, filter and density tools as quiet round buttons, like expand and collapse.
      '& .MuiButton-root': { minWidth: 0, width: 32, height: 32, p: 0, borderRadius: 999, border: `1px solid ${T.line}`, color: T.ink2 },
      '& .MuiButton-root .MuiButton-startIcon': { m: 0 },
      '& .MuiButton-root .MuiSvgIcon-root': { fontSize: 18 },
    }}
  >
    {p.configurations?.length && p.configurationKey ? (
      <>
        <ConfigurationSelect configurations={p.configurations} value={p.configurationKey} onChange={p.onConfigurationChange} counts={p.configurationCounts} />
        <Box sx={{ width: '1px', height: 24, bgcolor: T.line, display: { xs: 'none', lg: 'block' } }} />
      </>
    ) : null}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {KIND_FILTERS.filter((k) => k.key === 'all' || (p.kindCounts[k.key] ?? 0) > 0).map((k) => {
        const on = p.kindFilter === k.key;
        return (
          <ButtonBase
            key={k.key}
            onClick={() => p.onKindFilter(k.key)}
            aria-pressed={on}
            sx={{ height: 30, px: 1.375, borderRadius: 999, gap: 0.75, fontSize: 13, border: `1px solid ${on ? T.navy : T.line}`, color: on ? T.navy : T.ink2, bgcolor: on ? '#F2F6FB' : '#fff', fontWeight: on ? 600 : 400 }}
          >
            {k.key === 'attention' && <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: T.amber }} />}
            {k.label}
            <Box component="span" sx={{ fontFamily: PREVIEW_MONO, fontSize: 12, color: T.muted }}>{p.kindCounts[k.key] ?? 0}</Box>
          </ButtonBase>
        );
      })}
    </Box>
    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      {p.configurations?.length ? (
        <Tooltip title="Show only the lines of the selected variant" arrow>
          <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 13, color: T.ink2, cursor: 'pointer', mr: 0.5 }}>
            <Switch
              size="small"
              checked={p.variantOnly}
              onChange={(e) => p.onVariantOnly(e.target.checked)}
              sx={{
                mr: 0.5,
                '& .MuiSwitch-thumb': { bgcolor: '#fff', boxShadow: '0 1px 2px rgba(15,23,42,.25)' },
                '& .MuiSwitch-track': { bgcolor: T.line, opacity: '1 !important' },
                '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${T.navy} !important` },
              }}
            />
            This variant only
          </Box>
        </Tooltip>
      ) : null}
      {p.hasTree && (
        <>
          <Tooltip title="Expand all" arrow><IconButton size="small" onClick={p.onExpandAll} aria-label="Expand all" sx={{ border: `1px solid ${T.line}`, color: T.ink2 }}><UnfoldMoreIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title="Collapse all" arrow><IconButton size="small" onClick={p.onCollapseAll} aria-label="Collapse all" sx={{ border: `1px solid ${T.line}`, color: T.ink2 }}><UnfoldLessIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        </>
      )}
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 32, px: 1.25, border: `1px solid ${T.line}`, borderRadius: 999, width: 160, color: T.muted }}>
        <SearchIcon sx={{ fontSize: 16 }} />
        <InputBase value={p.search} onChange={(e) => p.onSearch(e.target.value)} placeholder="Search" inputProps={{ 'aria-label': 'Search part, drawing or description' }} sx={{ fontSize: 13, flex: 1 }} />
      </Box>
    </Box>
  </GridToolbarContainer>
);

export interface BomPreviewDataGridProps {
  rows: ParsedRow[];
  /** The mapped columns selected by the user in the column-mapping editor. */
  mappedColumns: ColumnMapping[];
  /** Available target fields, used to label mapped-data columns. */
  targetFields: Array<{ value: string; label: string }>;
  excludedRows: Set<number>;
  toggleRowExclusion: (rowIndex: number) => void;
  getRowStatusIcon: (row: ParsedRow) => React.ReactNode;
  getConfidenceBadge: (confidence: number) => React.ReactNode;
  busyRows: Set<string>;
  classificationOptions: PartClassificationOption[];
  documentChipRenderer: (props: {
    row: ParsedRow;
    busy: boolean;
    onUpload: (file: File, type: PreviewDocumentType) => void;
    onDetach: (documentId: string) => void;
  }) => React.ReactNode;
  onClassificationChange: (lineItemId: string, value: PartClassificationCode | '') => void;
  onSourcingModeToggle: (lineItemId: string, includeDiscovery: boolean) => void;
  /** The BOM-level policy each row inherits unless it discards or overrides. */
  bomPolicy?: SourcingConstraints;
  onConstraintsChange: (
    lineItemId: string,
    mode: ConstraintMode,
    constraints?: SourcingConstraints,
  ) => Promise<void> | void;
  /** The BOM's alternatives choice, which a row follows unless it has its own. */
  bomAlternativesSearch?: AlternativesSearchChoice;
  /** Set a row's own alternatives choice; null = it follows the BOM. */
  onAlternativesSearchChange: (lineItemId: string, value: AlternativesSearchChoice) => Promise<void> | void;
  /** The BOM's compliance choice, which a row follows unless it has its own (null = OFF). */
  bomComplianceCheck?: ComplianceCheckChoice;
  /** Set a row's own compliance choice; null = it follows the BOM. */
  onComplianceCheckChange?: (lineItemId: string, value: ComplianceCheckChoice) => Promise<void> | void;
  /** Set one or more sourcing goals of a row; null clears a goal. */
  onGoalsChange: (lineItemId: string, goals: LineGoals) => void;
  onDocumentUpload: (lineItemId: string, file: File, type: PreviewDocumentType) => void;
  onDocumentDetach: (lineItemId: string, documentId: string) => void;
  /** A multi-configuration ("150%") BOM: its variants. The table shows the chosen one's quantities. */
  configurations?: BomConfiguration[];
  /** Currency of the file's prices. */
  currency?: string;
  /** Change cells of a line by the file's column names; resolves to the line as read again. Absent = lines cannot be changed. */
  onCellsChange?: (lineItemId: string, cells: Record<string, string>) => Promise<ParsedRow>;
}

const BomPreviewDataGrid: React.FC<BomPreviewDataGridProps> = ({
  rows,
  mappedColumns,
  targetFields,
  excludedRows,
  toggleRowExclusion,
  getRowStatusIcon,
  getConfidenceBadge,
  busyRows,
  classificationOptions,
  documentChipRenderer,
  onClassificationChange,
  onSourcingModeToggle,
  bomPolicy,
  onConstraintsChange,
  onGoalsChange,
  bomAlternativesSearch,
  onAlternativesSearchChange,
  bomComplianceCheck,
  onComplianceCheckChange,
  onDocumentUpload,
  onDocumentDetach,
  configurations,
  currency,
  onCellsChange,
}) => {
  const tree = React.useMemo(() => buildPreviewTree(rows), [rows]);
  const byIndex = React.useMemo(() => new Map(rows.map((r) => [r.rowIndex, r])), [rows]);
  const [configurationKey, setConfigurationKey] = React.useState<string | undefined>(
    () => configurations?.find((c) => c.isDefault)?.key ?? configurations?.[0]?.key,
  );
  React.useEffect(() => {
    if (configurations?.length && !configurations.some((c) => c.key === configurationKey)) {
      setConfigurationKey(configurations.find((c) => c.isDefault)?.key ?? configurations[0].key);
    }
  }, [configurations, configurationKey]);
  const [kindFilter, setKindFilter] = React.useState<KindFilter>('all');
  const [variantOnly, setVariantOnly] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [collapsed, setCollapsed] = React.useState<Set<number | string>>(new Set());
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [selected, setSelected] = React.useState<number | null>(null);

  const inVariant = React.useCallback(
    (r: ParsedRow): boolean =>
      !configurationKey
        ? true
        : lineKind(r) === 'assembly'
          ? descendantsOf(tree, r.rowIndex).some((i) => { const c = byIndex.get(i); return !!c && lineKind(c) !== 'assembly' && quantityIn(c, configurationKey) != null; })
          : quantityIn(r, configurationKey) != null,
    [configurationKey, tree, byIndex],
  );
  const passes = React.useCallback(
    (r: ParsedRow): boolean => {
      if (kindFilter === 'attention' ? !needsAttention(r) : kindFilter === 'edited' ? !isEdited(r) : kindFilter !== 'all' && lineKind(r) !== kindFilter) return false;
      if (variantOnly && !inVariant(r)) return false;
      const q = search.trim().toLowerCase();
      if (q) {
        const md = r.mappedData ?? {};
        const text = `${md.partNumber ?? ''} ${md.drawingNumber ?? ''} ${md.partDescription ?? ''} ${md.manufacturerPartNumber ?? md.mpn ?? ''}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    },
    [kindFilter, variantOnly, inVariant, search],
  );
  // The tree is drawn while the lines are in file order and nothing narrows them; sorting,
  // a search or a kind filter shows the matching lines flat.
  const treeMode = tree.hasTree && !sortModel.length && kindFilter === 'all' && !search.trim();
  const shownRows: GridRow[] = React.useMemo(() => {
    if (!treeMode) return rows.filter(passes);
    const hidden = (r: ParsedRow): boolean => {
      for (let p = tree.parentOf.get(r.rowIndex); p != null; p = tree.parentOf.get(p)) if (collapsed.has(p)) return true;
      return false;
    };
    const unplaced = new Set(tree.unplaced);
    const placed = rows.filter((r) => !unplaced.has(r.rowIndex) && !hidden(r) && passes(r));
    const rest = rows.filter((r) => unplaced.has(r.rowIndex) && passes(r));
    if (!rest.length) return placed;
    const head: GridRow = { rowIndex: GROUP_ROW_INDEX, rawData: {}, __group: true };
    return [...placed, head, ...(collapsed.has(GROUP_KEY) ? [] : rest)];
  }, [treeMode, rows, passes, tree, collapsed]);
  const toggle = (key: number | string) =>
    setCollapsed((prev) => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  const configurationCounts = React.useMemo(
    () => Object.fromEntries((configurations ?? []).map((c) => [c.key, rows.filter((r) => lineKind(r) !== 'assembly' && lineKind(r) !== 'document' && quantityIn(r, c.key) != null).length])),
    [configurations, rows],
  );
  const kindCounts = React.useMemo(() => {
    const counts: Record<string, number> = { all: rows.length, attention: rows.filter(needsAttention).length, edited: rows.filter(isEdited).length };
    for (const r of rows) counts[lineKind(r)] = (counts[lineKind(r)] ?? 0) + 1;
    return counts;
  }, [rows]);
  const unplacedShown = tree.unplaced.filter((i) => { const r = byIndex.get(i); return !!r && passes(r); }).length;

  const columns: GridColDef<ParsedRow>[] = React.useMemo(() => {
    const labelFor = (targetField: string): string =>
      targetFields.find((f) => f.value === targetField)?.label || targetField;

    /** mapped__<source column> -> the field it is mapped to. */
    const targetOf = new Map<string, string>();
    const cols: GridColDef<ParsedRow>[] = [
      {
        field: '__include',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        colSpan: (_value, row, _column, api) => ((row as GridRow).__group ? api.current.getVisibleColumns().length : 1),
        renderCell: (params: GridRenderCellParams<ParsedRow>) =>
          (params.row as GridRow).__group ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: T.ink2, fontSize: 13 }}>
              <ExpandMoreIcon sx={{ fontSize: 18, color: T.muted, transition: 'transform 150ms', transform: collapsed.has(GROUP_KEY) ? 'rotate(-90deg)' : 'none' }} />
              Not on any assembly drawing
              <Box component="span" sx={{ fontFamily: PREVIEW_MONO, color: T.muted, fontWeight: 500 }}>{unplacedShown}</Box>
            </Box>
          ) : (
            <SmallCheckbox
              checked={!excludedRows.has(params.row.rowIndex) && (params.row.isValid ?? true)}
              onChange={() => toggleRowExclusion(params.row.rowIndex)}
              inputProps={{ 'aria-label': `Import ${params.row.mappedData?.partNumber ?? 'line'}` }}
            />
          ),
      },
      {
        field: '__num',
        headerName: '#',
        width: 60,
        sortable: true,
        filterable: false,
        valueGetter: (_value, row) => (row.rowIndex ?? 0) + 1,
        renderCell: (params: GridRenderCellParams<ParsedRow>) => (
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {(params.row.rowIndex ?? 0) + 1}
          </Typography>
        ),
      },
      {
        field: '__policy',
        headerName: 'Policy',
        description: 'The line follows the BOM policy unless a chip shows its own',
        // Right after the line number, so a line's policy (sourcing, alternatives,
        // compliance) is set without scrolling. Narrow on purpose: inheriting rows render a faint icon only. The BOM
        // policy is configured directly above this grid, so repeating it on
        // every row is redundant — only a deviation earns a visible chip.
        width: 92,
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) =>
          (row.constraintMode as ConstraintMode | undefined) ?? 'INHERIT',
        renderCell: (params: GridRenderCellParams<ParsedRow>) => {
          const lineId = params.row.lineItemId;
          // DOCUMENT lines are never sourced, so a policy on them is
          // meaningless — no chip rather than a misleading "inherited".
          if (!lineId || params.row.lineType === 'DOCUMENT') return null;
          return (
            <SourcingPolicyControl
              label={
                `${params.row.mappedData?.partNumber ?? ''} ` +
                `${params.row.mappedData?.partDescription ?? ''}`
              }
              resolved={effectiveForLine(bomPolicy, {
                constraintMode: params.row.constraintMode as ConstraintMode | undefined,
                sourcingConstraints: params.row.sourcingConstraints,
              })}
              saving={busyRows.has(lineId)}
              onSave={(mode, constraints) => onConstraintsChange(lineId, mode, constraints)}
              alternativesSearch={params.row.alternativesSearch ?? null}
              inheritedAlternativesSearch={bomAlternativesSearch ?? null}
              onAlternativesSearchChange={(value) => onAlternativesSearchChange(lineId, value)}
              complianceCheck={params.row.complianceCheck ?? null}
              inheritedComplianceCheck={bomComplianceCheck ?? null}
              onComplianceCheckChange={
                onComplianceCheckChange ? (value) => onComplianceCheckChange(lineId, value) : undefined
              }
            />
          );
        },
      },
      {
        field: '__status',
        headerName: 'Check',
        description: 'What to check on the line; the full text is on the line itself',
        width: 180,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (_value, row) => lineNotes(row).filter((n) => !n.info).length,
        // Quiet when a row is fine: only what needs a look is shown, in a few words.
        renderCell: (params: GridRenderCellParams<ParsedRow>) => {
          const notes = lineNotes(params.row).filter((n) => !n.info);
          if (!notes.length) return null;
          const error = notes.some((n) => n.error);
          return (
            <Tooltip title={notes.map((n) => n.text).join(' · ')} arrow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: error ? T.red : T.amber, fontSize: 12.5, minWidth: 0 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: error ? T.red : T.amber, flex: 'none' }} />
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {notes[0].short}{notes.length > 1 ? ` +${notes.length - 1}` : ''}
                </Box>
              </Box>
            </Tooltip>
          );
        },
      },
      // ── Slice -1 per-row controls (Type / Docs / Discovery) ────────
      // These sit immediately after Status so they remain visible
      // without horizontal scrolling. Hide them via column visibility
      // toggle in the toolbar if the BOM is purely catalog.
      {
        field: '__type',
        headerName: 'Type',
        width: 116,
        sortable: true,
        filterable: true,
        valueGetter: (_value, row) =>
          row.lineType === 'DOCUMENT'
            ? 'DOCUMENT'
            : row.partClassificationOverride
              ? String(row.partClassificationOverride)
              : '',
        renderCell: (params: GridRenderCellParams<ParsedRow>) => {
          const lineId = params.row.lineItemId;
          const cls = params.row.partClassificationOverride;
          const busy = lineId ? busyRows.has(lineId) : false;
          // Customer-package DOCUMENT line (process spec / work instruction):
          // read-only — it pins a registry document and never becomes a part,
          // so classification doesn't apply.
          if (params.row.lineType === 'DOCUMENT') {
            return (
              <Tooltip title="Process spec / work instruction from the customer package — related to its parent part and attached to that part's RFQs; never sourced or enriched on its own">
                <Chip
                  label="DOCUMENT"
                  size="small"
                  sx={{
                    bgcolor: alpha('#6366f1', 0.12),
                    color: '#6366f1',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: 20,
                  }}
                />
              </Tooltip>
            );
          }
          if (!lineId) {
            return (
              <Typography variant="caption" sx={{ color: '#cbd5e1', fontStyle: 'italic' }}>
                no lineId
              </Typography>
            );
          }
          return (
            <Select
              value={cls || ''}
              onChange={(e) =>
                onClassificationChange(
                  lineId,
                  (e.target.value as PartClassificationCode) || '',
                )
              }
              size="small"
              variant="standard"
              disableUnderline
              disabled={busy}
              displayEmpty
              renderValue={(val) => {
                // An empty classification is a DECISION, not missing data: the line showed no
                // custom-part signal, so it is an off-the-shelf part sent to market discovery.
                const kind = lineKind({ ...params.row, partClassificationOverride: (val || undefined) as PartClassificationCode | undefined });
                const opt = classificationOptions.find((o) => o.value === val);
                const label = kind === 'assembly' ? 'Assembly' : kind === 'make' ? 'Make' : 'Buy';
                return (
                  <Tooltip title={opt ? opt.label : 'Off-the-shelf catalog part: sourced through market discovery. Change it here if it is made to a drawing.'} arrow>
                    <Box component="span" sx={{ height: 22, px: 1, borderRadius: 1.5, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', ...(kind === 'assembly' ? { border: `1px solid ${T.navy}`, color: T.navy, bgcolor: '#fff' } : { bgcolor: T.soft, color: T.ink2 }) }}>
                      {label}
                    </Box>
                  </Tooltip>
                );
              }}
              sx={{
                minWidth: 0,
                fontSize: '0.75rem',
                '& .MuiSelect-select': { py: 0.25, pr: '22px !important', display: 'flex', alignItems: 'center' },
                // The menu arrow shows on the row the pointer is on.
                '& .MuiSelect-icon': { opacity: 0, transition: 'opacity 120ms', color: T.muted },
                '.MuiDataGrid-row:hover & .MuiSelect-icon, &.Mui-focused .MuiSelect-icon': { opacity: 1 },
              }}
            >
              <MenuItem value="">
                <em>Auto / parser default</em>
              </MenuItem>
              {classificationOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: opt.color,
                      }}
                    />
                    {opt.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          );
        },
      },
      {
        field: '__docs',
        headerName: 'Files',
        description: 'Files that came with the line; open the line to attach or remove one',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<ParsedRow>) => {
          const lineId = params.row.lineItemId;
          if (params.row.lineType === 'DOCUMENT') {
            return (
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                pinned
              </Typography>
            );
          }
          if (!lineId) {
            return (
              <Typography variant="caption" sx={{ color: '#cbd5e1', fontStyle: 'italic' }}>
                no lineId
              </Typography>
            );
          }
          const exts = [...new Set((params.row.pendingDocuments ?? []).map((d) => (d.fileName.split('.').pop() ?? '').toUpperCase()))].sort();
          return exts.length ? (
            <Tooltip title={(params.row.pendingDocuments ?? []).map((d) => d.fileName).join(' · ')} arrow>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {exts.map((e) => (
                  <Box key={e} component="span" sx={{ fontFamily: PREVIEW_MONO, fontSize: 10.5, color: T.muted, border: `1px solid ${T.line}`, borderRadius: 1.25, px: 0.625, lineHeight: '16px' }}>{e}</Box>
                ))}
              </Box>
            </Tooltip>
          ) : (
            <Box component="span" sx={{ color: T.faint }}>–</Box>          );
        },
      },
    ];

    // Sourcing goals, optional per line. Each shows what was typed here, else
    // what the file's own column says; an empty cell means no goal.
    const leadUnit = leadTimeUnitDays(
      mappedColumns.find((c) => c.targetField === 'targetLeadTime')?.sourceColumn,
    );
    const goalOf = (row: ParsedRow, key: keyof LineGoals): number | undefined => {
      const typed = row.goals ?? {};
      if (key in typed) return typed[key] ?? undefined;
      if (key === 'targetLeadTimeDays') {
        const fromFile = cellNumber(row.mappedData?.targetLeadTime);
        return fromFile != null ? Math.round(fromFile * leadUnit) : undefined;
      }
      return cellNumber(row.mappedData?.[key]);
    };
    const goalColumn = (
      key: keyof LineGoals,
      headerName: string,
      description: string,
      placeholder?: (row: ParsedRow) => string,
    ): GridColDef<ParsedRow> => ({
      field: `__goal_${key}`,
      headerName,
      description,
      width: 124,
      align: 'right',
      headerAlign: 'right',
      headerClassName: 'goal-header',
      sortable: true,
      filterable: false,
      valueGetter: (_value, row) => goalOf(row, key) ?? null,
      renderCell: (params: GridRenderCellParams<ParsedRow>) => {
        const lineId = params.row.lineItemId;
        if (!lineId || params.row.lineType === 'DOCUMENT') return null;
        return (
          <GoalCell
            value={goalOf(params.row, key)}
            placeholder={placeholder?.(params.row)}
            label={headerName}
            onCommit={(next) => onGoalsChange(lineId, { [key]: next })}
          />
        );
      },
    });
    cols.push(
      goalColumn(
        'requiredQuantity',
        'Required qty',
        'How many to buy. Optional; when empty, the line quantity is used.',
        (row) => String(row.mappedData?.quantity ?? ''),
      ),
      goalColumn('targetPrice', 'Target price', 'Goal price per unit. Optional.'),
      goalColumn('targetLeadTimeDays', 'Target lead time', 'Goal lead time in days. Optional.'),
    );

    // Pre-mapped imports (customer packages) carry no column mapping — show
    // identity columns straight off mappedData so every row is recognizable
    // (item id, description, revision, qty).
    if (mappedColumns.filter((c) => c.targetField).length === 0) {
      const md = (row: ParsedRow): Record<string, any> => row.mappedData ?? {};
      cols.push(
        {
          field: '__item',
          headerName: 'Item',
          minWidth: 300,
          flex: 1.4,
          sortable: true,
          filterable: true,
          valueGetter: (_value, row) =>
            String(md(row).partNumber ?? (row.lineType === 'DOCUMENT' ? 'Document' : '')),
          renderCell: (params: GridRenderCellParams<ParsedRow>) => {
            const row = params.row;
            const pn = String(md(row).partNumber ?? (row.lineType === 'DOCUMENT' ? 'Document' : ''));
            const description = !sameText(md(row).partDescription, pn) ? String(md(row).partDescription ?? '') : '';
            const drawing = md(row).drawingNumber && !sameText(md(row).drawingNumber, pn) && !sameText(md(row).drawingNumber, md(row).partDescription) ? String(md(row).drawingNumber) : '';
            const depth = treeMode ? tree.depthOf.get(row.rowIndex) ?? 0 : 0;
            const kids = treeMode ? tree.childrenOf.get(row.rowIndex) ?? [] : [];
            const assembly = lineKind(row) === 'assembly';
            const parts = assembly ? descendantsOf(tree, row.rowIndex).filter((k) => lineKind(byIndex.get(k) as ParsedRow) !== 'assembly').length : 0;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, height: '100%' }}>
                {Array.from({ length: depth }, (_, k) => (
                  <Box key={k} sx={{ width: 18, alignSelf: 'stretch', flex: 'none', position: 'relative', '&::before': { content: '""', position: 'absolute', left: 8, top: 0, bottom: 0, borderLeft: `1px solid ${T.line}` } }} />
                ))}
                {kids.length > 0 ? (
                  <IconButton
                    size="small"
                    aria-label={collapsed.has(row.rowIndex) ? 'Show parts' : 'Hide parts'}
                    aria-expanded={!collapsed.has(row.rowIndex)}
                    onClick={(e) => { e.stopPropagation(); toggle(row.rowIndex); }}
                    sx={{ p: 0.25, color: T.muted, flex: 'none' }}
                  >
                    <ExpandMoreIcon sx={{ fontSize: 18, transition: 'transform 150ms', transform: collapsed.has(row.rowIndex) ? 'rotate(-90deg)' : 'none' }} />
                  </IconButton>
                ) : treeMode ? (
                  <Box sx={{ width: 22, flex: 'none' }} />
                ) : null}
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13.5, fontWeight: assembly ? 700 : 600, color: T.ink, fontVariantNumeric: 'tabular-nums', lineHeight: 1.35 }}>
                    {pn}
                    {assembly && parts > 0 && (
                      <Box component="span" sx={{ ml: 0.75, fontSize: 12, fontWeight: 500, color: T.muted }}>{parts} parts</Box>
                    )}
                  </Typography>
                  {description && (
                    <Typography noWrap dir="auto" title={description} sx={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.35 }}>{description}</Typography>
                  )}
                  {drawing && <Typography noWrap sx={{ fontSize: 11.5, color: T.faint, lineHeight: 1.3 }}>{drawing}</Typography>}
                </Box>
              </Box>
            );
          },
        },
        {
          field: '__description',
          headerName: 'Description',
          minWidth: 240,
          flex: 1,
          sortable: true,
          filterable: true,
          valueGetter: (_value, row) => String(md(row).partDescription ?? ''),
          renderCell: (params: GridRenderCellParams<ParsedRow>) => (
            <Typography variant="body2" noWrap dir="auto" sx={{ fontSize: '0.8rem' }}>
              {String(md(params.row).partDescription ?? '')}
            </Typography>
          ),
        },
        {
          field: '__rev',
          headerName: 'Rev',
          width: 70,
          sortable: true,
          filterable: true,
          valueGetter: (_value, row) =>
            String(md(row).revision ?? md(row).documentRev ?? ''),
        },
        {
          field: '__qty',
          headerName: 'Qty',
          description: configurationKey ? 'Quantity in the selected variant' : undefined,
          width: 80,
          align: 'right',
          headerAlign: 'right',
          sortable: true,
          filterable: false,
          valueGetter: (_value, row) =>
            row.lineType === 'DOCUMENT' ? null : quantityIn(row, configurationKey) ?? null,
          renderCell: (params: GridRenderCellParams<ParsedRow>) => {
            if (params.row.lineType === 'DOCUMENT') return null;
            const q = quantityIn(params.row, configurationKey);
            return q == null ? (
              <Tooltip title="Not in this variant" arrow><Box component="span" sx={{ color: T.faint }}>–</Box></Tooltip>
            ) : (
              <span>{q}</span>
            );
          },
        },
        {
          field: '__material',
          headerName: 'Category',
          minWidth: 110,
          flex: 0.6,
          sortable: true,
          filterable: true,
          valueGetter: (_value, row) => String(md(row).materialType ?? ''),
          renderCell: (params: GridRenderCellParams<ParsedRow>) => {
            const v = md(params.row).materialType;
            if (isLostText(v)) {
              return (
                <Tooltip title={`The file has "${v}" here: the text was lost when the sheet was saved`} arrow>
                  <Box component="span" sx={{ color: T.faint, fontSize: 12.5 }}>Unreadable</Box>
                </Tooltip>
              );
            }
            return v ? <Typography noWrap sx={{ fontSize: 13, color: T.ink2 }}>{String(v)}</Typography> : <Box component="span" sx={{ color: T.faint }}>–</Box>;
          },
        },
        {
          field: '__mpn',
          headerName: 'Maker part no.',
          minWidth: 140,
          flex: 0.7,
          sortable: true,
          filterable: true,
          valueGetter: (_value, row) => String(md(row).manufacturerPartNumber ?? md(row).mpn ?? ''),
          renderCell: (params: GridRenderCellParams<ParsedRow>) => {
            const v = String(md(params.row).manufacturerPartNumber ?? md(params.row).mpn ?? '');
            return v ? <Typography noWrap sx={{ fontSize: 13, color: T.ink2, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography> : <Box component="span" sx={{ color: T.faint }}>–</Box>;
          },
        },
        {
          field: '__price',
          headerName: currency ? `Unit price (${currency})` : 'Unit price',
          width: 120,
          align: 'right',
          headerAlign: 'right',
          sortable: true,
          filterable: false,
          valueGetter: (_value, row) => cellNumber(md(row).unitCost) ?? null,
          renderCell: (params: GridRenderCellParams<ParsedRow>) => {
            const v = cellNumber(md(params.row).unitCost);
            return v != null ? <span>{v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> : <Box component="span" sx={{ color: T.faint }}>–</Box>;
          },
        },
      );
    }

    // Mapped data columns — one per column the user mapped in the
    // column-mapping editor. Title = canonical label; subtitle = the
    // source-file column name (shown via column description tooltip).
    for (const col of mappedColumns) {
      if (!col.targetField) continue;
      // A goal column of the file is shown, editable, in its goal column above.
      if (GOAL_TARGETS.has(col.targetField)) continue;
      const numeric = NUMERIC_TARGETS.has(col.targetField);
      targetOf.set(`mapped__${col.sourceColumn}`, col.targetField);
      cols.push({
        field: `mapped__${col.sourceColumn}`,
        headerName: labelFor(col.targetField),
        description: `Source column: ${col.sourceColumn}`,
        minWidth: numeric ? 96 : 140,
        flex: col.targetField === 'partDescription' ? 2 : numeric ? 0.6 : 1,
        align: numeric ? 'right' : 'left',
        headerAlign: numeric ? 'right' : 'left',
        sortable: true,
        filterable: true,
        // Double-click changes the file's cell; the line is read through the mapping again.
        editable: !!onCellsChange,
        valueGetter: (_value, row) => String(row.rawData?.[col.sourceColumn] ?? ''),
        valueSetter: (value, row) => ({ ...row, rawData: { ...row.rawData, [col.sourceColumn]: value ?? '' } }),
        renderCell: (params: GridRenderCellParams<ParsedRow>) => {
          const text = String(params.row.rawData?.[col.sourceColumn] ?? '');
          const was = params.row.originalCells && col.sourceColumn in params.row.originalCells ? params.row.originalCells[col.sourceColumn] : undefined;
          const unread = col.targetField === 'quantity' && text.trim() !== '' && !Number.isFinite(Number(params.row.mappedData?.quantity));
          return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, maxWidth: '100%' }}>
              {was !== undefined && (
                <Tooltip title={`Was: ${String(was ?? '').trim() || 'empty'}`} arrow>
                  <Box component="span" aria-label="Edited" sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: T.link, flex: 'none' }} />
                </Tooltip>
              )}
              {/* dir="auto" so a right-to-left value (Hebrew/Arabic part descriptions
                  are common on purchasing sheets) renders in its own direction
                  instead of showing reversed punctuation and misplaced digits. */}
              <Typography variant="body2" noWrap dir="auto" sx={{ fontSize: '0.8rem', color: unread ? T.red : undefined }}>
                {text}
              </Typography>
            </Box>
          );
        },
      });
    }

    cols.push({
      field: '__confidence',
      headerName: 'Confidence',
      width: 110,
      sortable: true,
      filterable: false,
      valueGetter: (_value, row) => row.confidence ?? 0,
      renderCell: (params: GridRenderCellParams<ParsedRow>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {getConfidenceBadge(params.row.confidence || 0)}
        </Box>
      ),
    });

    // Reading order: what the line IS, then its goals, then its settings, then
    // the rest of the file's columns. Identity and goals stay in view on a
    // wide file; the settings sit right after them.
    // Reading order: the line (item number, maker, maker part number, description, quantity,
    // type and its facts), its files, what to check, its policy and goals, then the rest of
    // the file's columns.
    // Item number, maker, its part number, description, quantity, then type (2026-10-28).
    const FIXED_ORDER = ['__include', '__num', '__item', '__mpn', '__description', '__qty', '__rev', '__type', '__material', '__price', '__docs', '__status', '__policy'];
    const group = (field: string): number => {
      if (FIXED_ORDER.includes(field)) return 0;
      const target = targetOf.get(field);
      if (target && IDENTITY_TARGETS.includes(target)) return 0;
      if (field.startsWith('__goal_')) return 1;
      if (field === '__confidence') return 3;
      return 2;
    };
    const within = (field: string): number => {
      if (FIXED_ORDER.includes(field)) return FIXED_ORDER.indexOf(field) * 10;
      const target = targetOf.get(field);
      // A mapped identity column (part number, maker part number, description, quantity) sits where its package twin sits.
      // The line's own item number comes first; the maker sits right before its part number.
      if (target === 'itemNumber' || target === 'findNumber') return FIXED_ORDER.indexOf('__num') * 10 + 1;
      if (target === 'manufacturerName') return FIXED_ORDER.indexOf('__mpn') * 10 - 1;
      const twin: Record<string, string> = { partNumber: '__item', localCode: '__item', partDescription: '__description', mpn: '__mpn', quantity: '__qty' };
      return target && twin[target] ? FIXED_ORDER.indexOf(twin[target]) * 10 + 1 : 0;
    };
    // The group row is drawn by the first column only.
    for (const col of cols) {
      if (col.field === '__include' || !col.renderCell) continue;
      const render = col.renderCell;
      col.renderCell = (params) => ((params.row as GridRow).__group ? null : render(params));
    }
    return cols
      .map((col, index) => ({ col, index }))
      .sort((a, b) => group(a.col.field) - group(b.col.field) || within(a.col.field) - within(b.col.field) || a.index - b.index)
      .map(({ col }) => col);
  }, [
    mappedColumns,
    targetFields,
    excludedRows,
    toggleRowExclusion,
    getRowStatusIcon,
    getConfidenceBadge,
    busyRows,
    classificationOptions,
    documentChipRenderer,
    onClassificationChange,
    onSourcingModeToggle,
    onGoalsChange,
    bomAlternativesSearch,
    onAlternativesSearchChange,
    bomComplianceCheck,
    onComplianceCheckChange,
    onDocumentUpload,
    onDocumentDetach,
    configurationKey,
    currency,
    onCellsChange,
    tree,
    treeMode,
    byIndex,
    collapsed,
    unplacedShown,
  ]);

  // Columns with nothing in them start hidden; every one stays in Columns.
  const columnVisibilityModel = React.useMemo(() => {
    const empty = (get: (r: ParsedRow) => unknown) => !rows.some((r) => { const v = get(r); return v != null && String(v).trim() !== ''; });
    const md = (r: ParsedRow) => r.mappedData ?? {};
    const packageView = !mappedColumns.some((c) => c.targetField);
    const model: Record<string, boolean> = { __num: false, __confidence: false };
    if (packageView) {
      model.__description = false;
      if (empty((r) => md(r).revision ?? md(r).documentRev)) model.__rev = false;
      if (empty((r) => md(r).materialType)) model.__material = false;
      if (empty((r) => md(r).manufacturerPartNumber ?? md(r).mpn)) model.__mpn = false;
      if (empty((r) => md(r).unitCost)) model.__price = false;
      if (empty((r) => r.goals?.requiredQuantity ?? md(r).requiredQuantity)) model.__goal_requiredQuantity = false;
      if (empty((r) => r.goals?.targetPrice ?? md(r).targetPrice)) model.__goal_targetPrice = false;
      if (empty((r) => r.goals?.targetLeadTimeDays ?? md(r).targetLeadTime)) model.__goal_targetLeadTimeDays = false;
    }
    return model;
    // Decided once per file: a column the buyer opens stays open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, mappedColumns]);
  const selectedRow = selected != null ? byIndex.get(selected) : undefined;
  /** Open the line before or after the open one, in the order the table shows them. */
  const stepFrom = (step: -1 | 1): (() => void) | undefined => {
    const shown = shownRows.filter((r) => !r.__group);
    const at = shown.findIndex((r) => r.rowIndex === selected);
    const to = at < 0 ? undefined : shown[at + step];
    return to ? () => setSelected(to.rowIndex) : undefined;
  };

  return (
    <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: selectedRow ? 'minmax(0, 1fr) 360px' : 'minmax(0, 1fr)' }, gap: 1.75, alignItems: 'start' }}>
      <Box sx={{ minWidth: 0 }}>
      <DataGrid
        rows={shownRows}
        columns={columns}
        getRowId={(row) => row.rowIndex}
        autoHeight
        disableRowSelectionOnClick
        density="standard"
        getRowHeight={() => 'auto'}
        columnHeaderHeight={42}
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 100, page: 0 } },
          columns: { columnVisibilityModel },
        }}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        isCellEditable={(params) => !!onCellsChange && !(params.row as GridRow).__group && !!params.row.lineItemId}
        processRowUpdate={async (next: ParsedRow, prev: ParsedRow) => {
          if (!onCellsChange || !prev.lineItemId) return prev;
          const cells: Record<string, string> = {};
          for (const c of mappedColumns) {
            const after = String(next.rawData?.[c.sourceColumn] ?? '');
            if (after.trim() !== String(prev.rawData?.[c.sourceColumn] ?? '').trim()) cells[c.sourceColumn] = after;
          }
          if (!Object.keys(cells).length) return prev;
          return onCellsChange(prev.lineItemId, cells);
        }}
        onProcessRowUpdateError={() => undefined}
        onRowClick={(params, event) => {
          if ((params.row as GridRow).__group) { toggle(GROUP_KEY); return; }
          // The second click of a double-click edits the cell; the line stays open.
          if (event.detail > 1) return;
          // Controls in a row (type, files, policy, goals) keep their own clicks.
          if ((event.target as HTMLElement).closest('button, input, a, label, [role="combobox"], [role="button"], .MuiSelect-select')) return;
          setSelected((prev) => (prev === params.row.rowIndex ? null : params.row.rowIndex));
        }}
        slots={{ toolbar: PreviewToolbar as never }}
        localeText={{ toolbarColumns: '', toolbarFilters: '', toolbarDensity: '' }}
        slotProps={{
          toolbar: {
            configurations,
            configurationKey,
            onConfigurationChange: setConfigurationKey,
            configurationCounts,
            kindFilter,
            onKindFilter: setKindFilter,
            kindCounts,
            variantOnly,
            onVariantOnly: setVariantOnly,
            hasTree: treeMode,
            onExpandAll: () => setCollapsed(new Set()),
            onCollapseAll: () => setCollapsed(new Set(rows.filter((r) => (tree.childrenOf.get(r.rowIndex)?.length ?? 0) > 0 && r.rowIndex !== rows[0]?.rowIndex).map((r) => r.rowIndex))),
            search,
            onSearch: setSearch,
          } as PreviewToolbarProps as never,
        }}
        sx={{
          border: 0,
          borderRadius: 0,
          bgcolor: '#fff',
          fontSize: '0.8125rem',
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            { outline: 'none' },
          '& .MuiDataGrid-toolbarContainer': { px: 1.5, py: 0.75, borderBottom: '1px solid #f1f5f9' },
          '& .goal-header .MuiDataGrid-columnHeaderTitle': { color: T.ink },
          // Right-aligned columns are numbers: on the right inside the flex cell, in the number face.
          '& .MuiDataGrid-cell--textRight': { justifyContent: 'flex-end', fontFamily: PREVIEW_MONO },
          '& .MuiDataGrid-cell--textRight .MuiTypography-root': { fontFamily: PREVIEW_MONO },
          // Vertically center cell content. DataGrid defaults align to
          // top-left; renderCell components (Select / Switch / Chip) look
          // off-axis without this. Applies to both default text cells and
          // custom renderers.
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 0.75,
            minHeight: 46,
            borderColor: '#f1f5f9',
          },
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-row.assembly-row': { bgcolor: '#FCFDFE' },
          '& .MuiDataGrid-row.group-row, & .MuiDataGrid-row.group-row:hover': { bgcolor: T.bg },
          '& .MuiDataGrid-row.selected-row, & .MuiDataGrid-row.selected-row:hover': { bgcolor: '#F2F6FB' },
          '& .MuiDataGrid-row.off-variant .MuiDataGrid-cell': { opacity: 0.45 },
          // Right-aligned columns keep their content on the right inside the flex cell.
          color: T.ink,
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: T.muted,
          },
          '& .MuiDataGrid-row:hover': {
            bgcolor: T.bg,
          },
          // Visually-mark invalid rows
          '& .MuiDataGrid-row.invalid-row': {
            bgcolor: alpha('#E74C3C', 0.03),
          },
        }}
        getRowClassName={(params) => {
          const row = params.row as GridRow;
          if (row.__group) return 'group-row';
          return [
            row.isValid === false ? 'invalid-row' : '',
            lineKind(row) === 'assembly' ? 'assembly-row' : '',
            row.rowIndex === selected ? 'selected-row' : '',
            configurationKey && !inVariant(row) ? 'off-variant' : '',
          ].filter(Boolean).join(' ');
        }}
      />
      </Box>
      {selectedRow && (
        <PreviewLineDetail
          row={selectedRow}
          rows={rows}
          tree={tree}
          configurations={configurations}
          configurationKey={configurationKey}
          currency={currency}
          filesControl={
            selectedRow.lineItemId && selectedRow.lineType !== 'DOCUMENT'
              ? documentChipRenderer({
                  row: selectedRow,
                  busy: busyRows.has(selectedRow.lineItemId),
                  onUpload: (file, type) => onDocumentUpload(selectedRow.lineItemId as string, file, type),
                  onDetach: (documentId) => onDocumentDetach(selectedRow.lineItemId as string, documentId),
                })
              : null
          }
          columnMapping={mappedColumns}
          onCellsChange={onCellsChange && selectedRow.lineItemId ? async (cells) => { await onCellsChange(selectedRow.lineItemId as string, cells); } : undefined}
          onPrevious={stepFrom(-1)}
          onNext={stepFrom(1)}
          onClose={() => setSelected(null)}
        />
      )}
    </Box>
  );
};

export default BomPreviewDataGrid;
